import { z } from 'zod'
import { note300, uuid } from './primitives.ts'
import { convert, parseDecimal, roundedText, UnitError } from './units.ts'

/** M15-B: the physical count (docs/M15.md B; benchmark 11, GK-026). The schemas mirror `core.command_type.payload_schema` in `20261212100300_stock_count.sql`;
 *  the contract check (ops/contracts/check.mjs) runs the fixtures and random payloads through both.
 *  - `stock.count_open@1` (an intent under stock.manage): a count of ONE place, at most one open per place.
 *  - `stock.count_line@1` (a FACT under stock.record, offline-first — the storeroom has no signal —, projected in `sync/projectors.count.ts`, kiosk-allowed by
 *    rule 8): one product counted, as typed (zero is an empty shelf); the id is the device's (two lines of one product both stay, the later typed is live).
 *  - `stock.count_close@1` (an intent under stock.manage): one count_adjustment per live line whose counted quantity differs from the place's on-hand AT
 *    CLOSE TIME, a reason each (the line's own, else one of `reasons`), COUNT_REASON_REQUIRED otherwise; flagged COUNT_MOVED_SINCE / COUNT_LINE_CONFLICT.
 *  The helpers below are what the device shows before the server answers (the server's rows are the truth): the base quantity a typed count will carry,
 *  whether a difference is one (the server's rule, `round(diff, 3) <> 0`), which line of a product is live, and the adjustment's derived id. Exact decimals. */

/** A count's reasons are at most this many characters (the line's column and the close's items). */
export const COUNT_REASON_MAX = 200
/** One close adjusts at most this many lines (stock.movement's line number for a count adjustment, and the close schema's maxItems). */
export const COUNT_MAX_ADJUSTMENTS = 2000
/** The derived id of the adjustment a line gets at the close, as the server derives it: v5(line_id, 'count'); the caller passes `derivedId` (ids.ts). */
export const COUNT_ADJUSTMENT_NAME = 'count'

const qty = z.number()
const unitText = z.string().min(1).max(20)

/** stock.count_open v1 (stock.manage, online): a count of one place of the club, with a note. */
export const stockCountOpenV1 = z.strictObject({ count_id: uuid, location_id: uuid, note: note300.optional() })
/** stock.count_line v1 (stock.record, a FACT): one product counted on an open count, as typed; the counter's reason optional. No lot: a count counts a product. */
export const stockCountLineV1 = z.strictObject({
  line_id: uuid, count_id: uuid, product_id: uuid, quantity: qty, unit: unitText, reason: z.string().max(COUNT_REASON_MAX).nullable().optional(),
})
export const stockCountReason = z.strictObject({ line_id: uuid, reason: z.string().min(1).max(COUNT_REASON_MAX) })
/** stock.count_close v1 (stock.manage, online): the reasons the closer typed for the lines that had none. */
export const stockCountCloseV1 = z.strictObject({ count_id: uuid, reasons: z.array(stockCountReason).max(COUNT_MAX_ADJUSTMENTS).optional() })

export type StockCountOpenV1 = z.infer<typeof stockCountOpenV1>
export type StockCountLineV1 = z.infer<typeof stockCountLineV1>
export type StockCountCloseV1 = z.infer<typeof stockCountCloseV1>
export type StockCountReason = z.infer<typeof stockCountReason>

// ---- what the device shows before the server answers ---------------------------------------------------------------------------------------
/** A typed count in the product's base unit through the units twin (the server converts again under the same version): zero allowed (an empty shelf),
 *  three decimals at most, pieces whole, at most ten million (QUANTITY_INVALID), or the unit code's refusal. */
export type CountPreview = { ok: true; countedBase: string } | { ok: false; code: UnitError['code'] | 'QUANTITY_INVALID' }
export function countPreview(quantity: string | number, unit: string, baseUnitCode: string, densityKgPerL: string | number | null, unitDimension: string | undefined): CountPreview {
  const text = String(quantity).trim().replace(',', '.')
  let v: ReturnType<typeof parseDecimal>
  try { v = parseDecimal(text) } catch { return { ok: false, code: 'QUANTITY_INVALID' } }
  if (v.n < 0n || (v.n * 1000n) % v.d !== 0n || v.n > 10_000_000n * v.d) return { ok: false, code: 'QUANTITY_INVALID' }
  if (unitDimension === 'count' && v.n % v.d !== 0n) return { ok: false, code: 'QUANTITY_INVALID' }
  try {
    const countedBase = convert(text, unit, baseUnitCode, densityKgPerL === null ? null : String(densityKgPerL))
    if (v.n > 0n && parseDecimal(countedBase).n <= 0n) return { ok: false, code: 'QUANTITY_INVALID' }
    return { ok: true, countedBase }
  } catch (e) { if (e instanceof UnitError) return { ok: false, code: e.code }; throw e }
}

const micro = (v: string | number): bigint => { const r = parseDecimal(String(v)); return (r.n * 1_000_000n) / r.d }
/** counted − expected, in millionths of the base unit, as text with six decimals; `differs` is the server's rule: the difference rounded to the three
 *  decimals a quantity is typed with (half away from zero, PostgreSQL's `round(x, 3)`) is not zero — a smaller one is no difference and asks no reason. */
export function countDifference(countedBase: string | number, expectedBase: string | number): { diff: string; differs: boolean } {
  const d = micro(countedBase) - micro(expectedBase)
  return { diff: roundedText({ n: d, d: 1_000_000n }, 6), differs: (d < 0n ? -d : d) >= 500n }
}

export interface LineLite { id: string; count_id: string; product_id: string; occurred_at: string }
/** Of the lines of one count, the LIVE one of each product: the latest typed (its instant, then its id), as the server keeps it whatever the order the lines
 *  reached it in (a phone that synced late) — the server marks the others `superseded_by_id`; the device reads the same rule from its copy and its pending lines. */
export function liveLines<T extends LineLite>(lines: readonly T[], countId: string): Map<string, T> {
  const out = new Map<string, T>()
  for (const l of lines) {
    if (l.count_id !== countId) continue
    const cur = out.get(l.product_id)
    if (!cur || later(l, cur)) out.set(l.product_id, l)
  }
  return out
}
/** Whether line a was typed after line b (the instant compared as an instant, then the id as PostgreSQL compares uuids: their lower-case hex). */
export function later(a: Pick<LineLite, 'id' | 'occurred_at'>, b: Pick<LineLite, 'id' | 'occurred_at'>): boolean {
  const ta = Date.parse(a.occurred_at), tb = Date.parse(b.occurred_at)
  if (ta !== tb) return ta > tb
  return a.id.toLowerCase() > b.id.toLowerCase()
}
